import { computed } from 'vue'

import type { ComputedRef, Ref } from 'vue'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'
import type { AppSelectProps } from '../types/interfaces'
import type { UseAppSelectCoreReturn } from '../types/core'

import { useAppSelectCore } from './use-app-select-core.hook'
import { createSingleSelectionAdapter } from './create-single-selection-adapter'

interface UseAppSelectOptions<TValue = string | number> {
  modelValue: Ref<TValue | undefined>
  props: AppSelectProps<TValue>
  onOpen: () => void
  onClose: () => void
}

interface UseAppSelectReturn<TValue = string | number>
  extends UseAppSelectCoreReturn<TValue> {
  /** Always shows the resolved selected option's text (no editing). */
  inputValue: ComputedRef<string>
  /** Falls back to props.placeholder when no option is selected. */
  inputPlaceholder: ComputedRef<string>
  /** Options to render in the panel (just `props.options`, computed for reactivity). */
  displayedOptions: ComputedRef<AppSelectOption<TValue>[]>
  /** Currently selected option (resolved by value) or undefined. Exposed for
   *  the `#selected` slot in the trigger. */
  resolvedSelectedOption: ComputedRef<AppSelectOption<TValue> | undefined>
}

export const useAppSelect = <TValue = string | number>(
  options: UseAppSelectOptions<TValue>,
): UseAppSelectReturn<TValue> => {
  const { modelValue, props, onOpen, onClose } = options

  const displayedOptions = computed<AppSelectOption<TValue>[]>(() => props.options)

  const resolvedSelectedOption = computed<AppSelectOption<TValue> | undefined>(
    () => props.options.find(o => o.value === modelValue.value),
  )

  const inputValue = computed<string>(
    () => resolvedSelectedOption.value?.text ?? '',
  )

  const inputPlaceholder = computed<string>(() => {
    if (resolvedSelectedOption.value) return ''
    return props.placeholder ?? ''
  })

  const selection = createSingleSelectionAdapter<TValue>({ modelValue, displayedOptions })

  const core = useAppSelectCore<TValue>({
    props,
    displayedOptions,
    mode: { inputEditable: false },
    selection,
    onOpen,
    onClose,
  })

  return {
    ...core,
    inputValue,
    inputPlaceholder,
    displayedOptions,
    resolvedSelectedOption,
  }
}
